import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Clock, ArrowRight } from 'lucide-react';
import axios from '../lib/axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API, useLanguage } from '../App';

// Promotion add-ons - REMOVED, now using dynamic boost packages from API

const PricingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [boostPackages, setBoostPackages] = useState([]);
  const [loadingBoost, setLoadingBoost] = useState(true);

  // Fetch boost packages from API
  useEffect(() => {
    const fetchBoostPackages = async () => {
      try {
        const response = await axios.get(`${API}/payments/boost-config`);
        setBoostPackages(response.data.packages || []);
      } catch (error) {
        console.error('Failed to fetch boost packages:', error);
      } finally {
        setLoadingBoost(false);
      }
    };
    fetchBoostPackages();
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8 md:pt-24 md:pb-12">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t('pricing_hero_title')}{' '}
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                {t('pricing_hero_highlight')}
              </span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              {t('pricing_hero_subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        
        {/* Free Listing Info */}
        <div className="mb-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-card rounded-2xl border-2 border-primary shadow-lg p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-white" />
              </div>
              
              <h2 className="text-2xl font-bold text-foreground mb-2">{t('pricing_free_title')}</h2>
              <p className="text-muted-foreground mb-6">{t('pricing_free_subtitle')}</p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {[
                  t('pricing_free_features_1'),
                  t('pricing_free_features_2'),
                  t('pricing_free_features_3'),
                  t('pricing_free_features_4'),
                  t('pricing_free_features_5'),
                  t('pricing_free_features_6')
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-left">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-foreground">{feature}</span>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => navigate('/create-listing')}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-all"
              >
                {t('pricing_free_cta')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Boost Packages */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-2">
            <Zap className="w-6 h-6 inline-block text-amber-500 mr-2" />
            {t('pricing_boost_title')}
          </h2>
          <p className="text-muted-foreground text-center mb-10">{t('pricing_boost_subtitle')}</p>
          
          {loadingBoost ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {boostPackages.map((pkg, index) => {
                const isPopular = index === 1; // Middle package is popular
                const durationLabel = pkg.duration_hours < 48 
                  ? `${pkg.duration_hours} ${t('pricing_hours')}` 
                  : `${Math.round(pkg.duration_hours / 24)} ${t('pricing_days')}`;
                
                // Use translated names/descriptions based on package ID
                const packageName = t(`pricing_boost_${pkg.id}_name`) || pkg.name;
                const packageDesc = t(`pricing_boost_${pkg.id}_desc`) || pkg.description;
                
                return (
                  <div
                    key={pkg.id}
                    className={`relative bg-card rounded-2xl border-2 p-6 transition-all hover:shadow-xl ${
                      isPopular ? 'border-amber-500 shadow-lg shadow-amber-500/10 scale-105' : 'border-border'
                    }`}
                    data-testid={`boost-package-${pkg.id}`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          {t('pricing_boost_popular')}
                        </span>
                      </div>
                    )}
                    
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-4">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-foreground">{packageName}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{packageDesc}</p>
                    
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-amber-500">
                          XCG {pkg.price.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        <Clock className="w-4 h-4 inline-block mr-1" />
                        {durationLabel} boost
                      </p>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-foreground">{t('pricing_boost_feature_1')}</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-foreground">{t('pricing_boost_feature_2')}</span>
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-foreground">{t('pricing_boost_feature_3')}</span>
                      </li>
                    </ul>
                    
                    <button
                      onClick={() => navigate('/my-listings')}
                      className={`w-full py-3 rounded-xl font-semibold transition-all ${
                        isPopular
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    >
                      {t('pricing_boost_cta')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Payment info */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              {t('pricing_boost_payment_info')} <span className="font-semibold">Sentoo</span> • {t('pricing_boost_instant')}
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-10">{t('pricing_faq_title')}</h2>
          
          <div className="space-y-4">
            {[
              { q: t('pricing_faq_1_q'), a: t('pricing_faq_1_a') },
              { q: t('pricing_faq_2_q'), a: t('pricing_faq_2_a') },
              { q: t('pricing_faq_3_q'), a: t('pricing_faq_3_a') },
              { q: t('pricing_faq_4_q'), a: t('pricing_faq_4_a') },
              { q: t('pricing_faq_5_q'), a: t('pricing_faq_5_a') },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-card rounded-xl border border-border p-5"
              >
                <h3 className="font-semibold text-foreground mb-2">{faq.q}</h3>
                <p className="text-sm text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              {t('pricing_cta_title')}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              {t('pricing_cta_subtitle')}
            </p>
            <button
              onClick={() => navigate('/create-listing')}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-all"
            >
              {t('pricing_cta_button')}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PricingPage;
